import Handlebars from 'handlebars';
import i18n from 'i18next';
import { html, render } from 'lit-html';
import { store } from '../../../app.init';
import {
  getDecimal,
  getUnit,
} from '../../../shared/components/chart/chart-helpers';
import { setAnalysis } from '../../../state/actions';
import { getState } from '../../../utils/state';
import { getUrlParams, setUrlParam } from '../../../utils/url';
import { Utils } from '../../../utils/utils';

import geoComposited from '../geographical/queries/geo_composited';
import geoOperationDiv from '../geographical/queries/geo_operation_div';
import geoSimple from '../geographical/queries/geo_simple';
import levelGeoComposited from '../geographical/queries/level_geo_composited';
import levelGeoOperationDiv from '../geographical/queries/level_geo_operation_div';
import levelGeoSimple from '../geographical/queries/level_geo_simple';

const AnalysisGeographical = (analysis, indicatorInfo, sqlParams, data) => {
  const indicatorLevel = indicatorInfo.nivel[0].geo_nivel;

  // Getting global common state
  const state = getState();
  // Current language we are using
  const lang = state.currentLang;
  const { level, zoom, latlng, geoids } = getUrlParams();
  const currentLevel =
    state.analysis.level || level || indicatorInfo.nivel[0].geo_nivel;
  let indicatorName = '',
    geosArr = '';
  if (state?.analysis?.indicator) {
    geosArr = state.analysis.indicator.geos;
    indicatorName = state.analysis.indicator.name;
  }
  let foundGeo = geoids && geosArr.find((el) => el.geo_id == geoids)?.geo_name;
  let preLatLang = latlng
    ? latlng.includes(',')
      ? latlng.split(',')
      : latlng.split('%2C')
    : [40, -3];

  let defaults = {
    colors: [
      '#ffffcc',
      '#c7e9b4',
      '#7fcdbb',
      '#41b6c4',
      '#1d91c0',
      '#225ea8',
      '#0c2c84',
    ],
    indicatorId: indicatorInfo.id,
    composited: indicatorInfo.composited,
    unit: getUnit(indicatorInfo.magnitud[0].id),
    decimal: getDecimal(indicatorInfo.magnitud[0].id) || 1,
    title: foundGeo ? `${indicatorName} ${foundGeo}` : '',
    currentIndicator: indicatorInfo,
    currentGeoIds: indicatorInfo.geos,
    dates: [sqlParams.start_date, sqlParams.end_date],
  };

  let create = false;
  const geoMapId = `geo-map-container-${indicatorInfo.id}`;
  document.getElementById(geoMapId).innerHTML = '';
  const mapWrp = document.getElementById(geoMapId);
  const newMap = document.createElement('div');
  newMap.classList.add('geo-map');
  newMap.setAttribute('id', 'geo-map-' + indicatorInfo.id);
  mapWrp.append(newMap);
  mapWrp.style.width = '100%';
  mapWrp.style.height = '400%';

  let quantiles = calculateQuantiles(data);
  loadGeoredComponent();
  initGoeRed(data, quantiles, indicatorLevel);
  addLegend(quantiles, defaults.unit);

  return html``;

  function calculateQuantiles(geoData) {
    if (state.analysis && state.analysis.quantiles) {
      return state.analysis.quantiles;
    } else {
      const maxScale = 7;
      const data = geoData.rows.map((g) => g.total);
      const colors = defaults.colors.reverse();
      var numberOfQuantiles = data.length <= maxScale ? data.length : maxScale;
      let quantiles = [];
      if (numberOfQuantiles <= 1) {
        quantiles = [
          {
            originalValue: data[0],
            value: Math.floor(data[0] / 10) * 10,
            color: colors[0],
            sign: '>',
          },
        ];
        // Else calculing quantils
      } else {
        let quantilesTemp = calcQuantils(data, numberOfQuantiles - 1, {})
          .reverse()
          .map(function (q, i) {
            // Client wish round to 10
            // OLD: var quantilValue = q < 10 ? Math.round(q) : Math.floor(q / 10) * 10;
            var quantilValue = Math.floor(q / 10) * 10;
            return {
              originalValue: q,
              value: quantilValue,
            };
          });
        _.uniq(quantilesTemp, true, 'value').map((q, i) => {
          quantiles.push({ ...q, color: colors[i], sign: '>' });
        });
      }

      return quantiles;
    }
  }

  function initGoeRed(markets, coloreslegend, indicatorLevel) {
    const keys = {
      country: {
        es: {
          Portugal: 'PRT',
          'Reino Unido': 'GBR',
          Bélgica: 'BEL',
          Alemania: 'DEU',
          'Países Bajos': 'NLD',
          Italia: 'ITA',
          España: 'ESP',
          Francia: 'FRA',
        },
        en: {
          Portugal: 'PRT',
          'United Kingdom': 'GBR',
          Belgium: 'BEL',
          Germany: 'DEU',
          Netherlands: 'NLD',
          Italy: 'ITA',
          Spain: 'ESP',
          France: 'FRA',
        },
      },
      electric: {
        es: {
          Peninsula: 'PEN',
          Península: 'PEN',
          Canarias: 'CAN',
          Baleares: 'BAL',
          Melilla: 'MEL',
          Ceuta: 'CEU',
        },
        en: {
          Peninsula: 'PEN',
          'Canary islands': 'CAN',
          'Balearic islands': 'BAL',
          Melilla: 'MEL',
          Ceuta: 'CEU',
        },
      },
      ccaa: {
        es: {
          Andalucía: '01',
          Aragon: '02',
          Aragón: '02',
          Asturias: '03',
          'Principado de Asturias': '03',
          'Islas Baleares': '04',
          'Islas Canarias': '05',
          Cantabria: '06',
          'Castilla y León': '07',
          'Castilla-La Mancha': '08',
          Cataluña: '09',
          Valencia: '10',
          'Comunidad Valenciana': '10',
          Extremadura: '11',
          Galicia: '12',
          'Comunidad de Madrid': '13',
          Murcia: '14',
          'Región de Murcia': '14',
          Navarra: '15',
          'Comunidad Foral de Navarra': '15',
          'Pais Vasco': '16',
          'País Vasco': '16',
          'La Rioja': '17',
          Melilla: '19',
          Ceuta: '18',
        },
        en: {
          Andalusia: '01',
          Aragon: '02',
          'Principado de Asturias': '03',
          'Balearic Islands': '04',
          'Canary Islands': '05',
          Cantabria: '06',
          'Castilla y León': '07',
          'Castilla-La Mancha': '08',
          Catalonia: '09',
          'Valencian Community': '10',
          Extremadura: '11',
          Galicia: '12',
          'Community of Madrid': '13',
          'Region of Murcia': '14',
          'Community of Navarra': '15',
          'Pais Vasco': '16',
          'País Vasco': '16',
          'La Rioja': '17',
          Melilla: '19',
          Ceuta: '18',
        },
      },
      province: {
        Álava: '01',
        Albacete: '02',
        Alicante: '03',
        Almería: '04',
        Ávila: '05',
        Badajoz: '06',
        Baleares: '07',
        Barcelona: '08',
        Burgos: '09',
        Cáceres: '10',
        Cádiz: '11',
        Castellón: '12',
        'Ciudad Real': '13',
        Córdoba: '14',
        'A Coruña': '15',
        Cuenca: '16',
        Girona: '17',
        Granada: '18',
        Guadalajara: '19',
        Gipuzkoa: '20',
        Huelva: '21',
        Huesca: '22',
        Jaén: '23',
        León: '24',
        Lleida: '25',
        'La Rioja': '26',
        Lugo: '27',
        Madrid: '28',
        Málaga: '29',
        Murcia: '30',
        Navarra: '31',
        Ourense: '32',
        Asturias: '33',
        Palencia: '34',
        'Las Palmas': '35',
        Pontevedra: '36',
        Salamanca: '37',
        'Santa Cruz de Tenerife': '38',
        Segovia: '40',
        Sevilla: '41',
        Soria: '42',
        Tarragona: '43',
        Teruel: '44',
        Toledo: '45',
        Valencia: '46',
        Valladolid: '47',
        Vizcaya: '48',
        Zaragoza: '50',
        Ceuta: '51',
        Melilla: '52',
      },
    };

    const loadGeoredComponent = () => {
      if (typeof getGeoredComponentById !== 'undefined') {
        debugger;
        let id = '600';
        if (state.analysis.indicator.id != null) {
          id = state.analysis.indicator.id;
        }
        let instance = getGeoredComponentById(`geo-map-container-${id}`);

        let language = window.location.href.includes('/en') ? 'en' : 'es';
        let level = state.analysis.levels;
        if (level == null) {
          level = state.analysis.level;
          level = Number(level);
          if (state.analysis.level == null) {
            level = indicatorLevel;
          }
        }
        let data = {};
        if (markets?.rows) {
          markets.rows.forEach(({ name, total }) => {
            let key;
            switch (level) {
              case 3:
                key = keys.province[name];
                break;
              case 2:
                if (language === 'en') {
                  key = keys.ccaa.en[name];
                } else {
                  key = keys.ccaa.es[name];
                }
                break;
              case 1:
                if (language === 'en') {
                  key = keys.electric.en[name];
                } else {
                  key = keys.electric.es[name];
                }
                break;
              default:
                if (language === 'en') {
                  key = keys.country.en[name];
                } else {
                  key = keys.country.es[name];
                }
            }

            if (key) {
              data[key] = parseFloat(total.toFixed(2));
            } else {
              console.log('Unknown market:', name);
            }
          });
        }

        const { min, max } = markets.rows.reduce(
          (acc, { total }) => {
            acc.min = Math.min(acc.min, total);
            acc.max = Math.max(acc.max, total);
            return acc;
          },
          { min: Infinity, max: -Infinity }
        );

        const colors = [
          '#ffffcc',
          '#c7e9b4',
          '#7fcdbb',
          '#41b6c4',
          '#1d91c0',
          '#225ea8',
          '#0c2c84',
        ];
        const reverseColors = [...colors].reverse();

        const calculateColorIndex = (value, min) =>
          Math.min(Math.floor((value - min) / 10), colors.length - 1);

        const coloredValues = markets.rows.map(({ total }) => {
          const index = calculateColorIndex(total, min);
          return {
            value: Math.floor(total),
            color: reverseColors[index],
            label: `val ${Math.floor(total)}`,
          };
        });

        const transformedArray = coloreslegend.map((item) => ({
          value: item.value,
          color: item.color,
          label: `val ${item.value}`,
        }));

        let colores = transformedArray;
        if (level == 0 && indicatorLevel != 0) {
          colores = ['#0c2c84', '#ffffcc'];
        }

        let representacion = 'world_countries';
        let magnitud = '€/MWh';
        if (state.analysis != null && state.analysis.indicator != null) {
          const magnitudes = {
            13: 'MWh',
            20: 'MW',
            21: 'MW',
            23: '€/MWh',
            24: '€',
            152: '%',
            153: '',
            209: 'tCO2',
            211: '€/tCO2',
            217: 'tCO2/MW',
            238: '€/ton',
          };

          magnitud =
            magnitudes[state.analysis.indicator.magnitud[0].id] || 'MW';
        }
        let content =
          '${name_' + language + '}: ${expression/thematicValue} ' + magnitud;
        if (level == 2) {
          representacion = 'ccaa';
          content = '${name}: ${expression/thematicValue} ' + magnitud;
        } else if (level == 3) {
          representacion = 'prov';
          content = '${name}: ${expression/thematicValue} ' + magnitud;
        } else if (level == 1) {
          representacion = 'sistema_electrico';
          content = '${name}: ${expression/thematicValue} ' + magnitud;
        }

        instance.removeService('capa_tematico_1');
        instance.addThematicData(data, {
          data_id: 'capa_tematico_1',
          representation_type: representacion,
          colors: colores,
          data_title: 'Capa de valores',
          value_title: 'Valor',
          popupContent: false,
          mapTip: {
            content: content,
            format: {
              font: { size: 13 },
              xoffset: 15,
              color: [2, 91, 136, 1],
              backgroundColor: [255, 255, 255, 255],
              borderLineColor: [24, 119, 220, 255],
              borderLineSize: 1,
            },
          },
          outline_color: '#ffffff',
          point_style: 'circle',
          point_size: 10,
        });
      } else {
        setTimeout(loadGeoredComponent, 5000); // Retry after 5 seconds
      }
    };

    setTimeout(loadGeoredComponent, 3000);
  }

  function loadGeoredComponent() {
    if (typeof getGeoredComponentById !== 'undefined') {
      georedcontroller.destroyAll();
      var nuevoMapa = createGeoredComponent(mapWrp, {
        appName: 'esios/spotdiario_peninsula',
      });
      create = true;
    } else {
      setTimeout(loadGeoredComponent, 2000); // Wait for 9 seconds and retry
    }
  }

  function calcQuantils(arr, num, opts) {
    let ascending = (a, b) => {
      return a - b;
    };
    var len = arr.length,
      qValues = new Array(num + 1),
      id,
      val;
    if (!opts.sorted) {
      arr = arr.slice();
      arr.sort(ascending);
    }
    // 0th quantile is the min:
    qValues[0] = arr[0];

    // Max defines the quantile upper bound:
    qValues[num] = arr[len - 1];

    // Get the quantiles...
    for (var i = 1; i < num; i++) {
      // Calculate the vector index marking the quantile:
      id = (len * i) / num - 1;

      // Is the index an integer?
      if (id === Math.floor(id)) {
        // Value is the average between the value at id and id+1:
        val = (arr[id] + arr[id + 1]) / 2.0;
      } else {
        // Round up to the next index:
        id = Math.ceil(id);
        val = arr[id];
      }
      qValues[i] = val;
    } // end FOR i
    return qValues;
  }

  function getLegendTemplate(legendData, title, unit) {
    return html`
      <div class="widget-legend basic-legend">
        ${title ? html`<h3>${title}</h3>` : ''}
        <ul>
          ${legendData &&
          legendData.length > 0 &&
          legendData.map((elem) => {
            return html`
              <li class="legend-item item-content">
                <div class="item">
                  <span
                    class="legend-circle"
                    .style="background-color: ${elem.color};"
                  ></span
                  >${elem.name}
                </div>
                <div class="value is-number">
                  <!-- CHECK IF ALWAYS SHOULD BE ZERO -->
                  ${elem.sign} ${Utils.toNumber(elem.value, 0, 'comma')}
                  <span class="unit">${unit}</span>
                </div>
              </li>
            `;
          })}
        </ul>
      </div>
    `;
  }
  function addLegend(quantiles, unit) {
    let legend = document.querySelector('#geo-legend');

    var legendData = quantiles.map(function (q) {
      return {
        sign: q.sign,
        value: q.value,
        color: q.color,
        unit: unit ? unit : '',
      };
    });

    // var clickEventName = window.ontouchstart ? 'touchstart' : 'click';
    render(
      getLegendTemplate(legendData, defaults.title, defaults.unit),
      legend
    );

    legend.classList.remove('is-hidden');

    var $btnLockLegend = legend.querySelector('.btn-lock-legend');
    var $btnUnlockLegend = legend.querySelector('.btn-unlock-legend');
    var $switch = legend.querySelector('.toggle-switch');
    if ($switch != null) {
      $switch.addEventListener('click', (e) => toggleLegend(e, quantiles));
      $btnLockLegend.addEventListener('click', function () {
        $switch.classList.add('locked');
        $btnLockLegend.style.opacity = 1;
        $btnUnlockLegend.style.opacity = 0.5;
        lockLegend(quantiles);
      });
      $btnUnlockLegend.addEventListener('click', function () {
        $switch.classList.remove('locked');
        $btnUnlockLegend.style.opacity = 1;
        $btnLockLegend.style.opacity = 0.5;
        unlockLegend();
      });

      if (state.analysis.quantiles) {
        $switch.classList.add('locked');
        $btnLockLegend.style.opacity = 1;
        $btnUnlockLegend.style.opacity = 0.5;
      } else {
        $switch.classList.remove('locked');
        $btnLockLegend.style.opacity = 0.5;
        $btnUnlockLegend.style.opacity = 1;
      }
    }
  }

  function toggleLegend(ev, quantiles) {
    var $element = ev.currentTarget;
    let legend = document.querySelector('#geo-legend');

    var $btnLockLegend = legend.querySelector('.btn-lock-legend');
    var $btnUnlockLegend = legend.querySelector('.btn-unlock-legend');

    if (!$element.classList.contains('locked')) {
      $element.classList.add('locked');
      $btnLockLegend.style.opacity = 1;
      $btnUnlockLegend.style.opacity = 0.5;
      lockLegend(quantiles);
    } else {
      $element.classList.remove('locked');
      $btnUnlockLegend.style.opacity = 1;
      $btnLockLegend.style.opacity = 0.5;
      unlockLegend();
    }
  }
  function lockLegend(quantiles) {
    store.dispatch(
      setAnalysis({
        quantiles: quantiles,
      })
    );
  }
  function unlockLegend() {
    store.dispatch(
      setAnalysis({
        quantiles: null,
      })
    );
  }
};

export default AnalysisGeographical;
